import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, CheckCircle2 } from "lucide-react";
import { API, apiError } from "../lib/api";
import { PageHeader, Spinner, Empty } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";

const statusColor = { Scheduled: "text-blue-400 bg-blue-500/10", Completed: "text-emerald-400 bg-emerald-500/10", Cancelled: "text-slate-400 bg-slate-500/10" };

export default function SiteVisits() {
  const [visits, setVisits] = useState(null);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ lead_id: "", project_id: "", associate_id: "", date: "", feedback: "", next_action: "" });

  const load = () => API.get("/site-visits").then((r) => setVisits(r.data));
  useEffect(() => { load(); API.get("/leads").then((r) => setLeads(r.data)); API.get("/projects").then((r) => setProjects(r.data)); API.get("/associates").then((r) => setAssociates(r.data)); }, []);

  const create = async () => {
    if (!form.lead_id || !form.date) { toast.error("Lead and date required"); return; }
    try { await API.post("/site-visits", form); toast.success("Site visit scheduled"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const complete = async (v) => { await API.put(`/site-visits/${v.id}`, { status: "Completed", outcome: "Positive" }); load(); toast.success("Site visit completed"); };

  if (!visits) return <Spinner />;

  return (
    <div>
      <PageHeader title="Site Visits" subtitle="Track project site visits & outcomes." testid="site-visits-header">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><button className="btn-gold px-4 py-2 flex items-center gap-2 text-sm" data-testid="new-visit-btn"><Plus className="h-4 w-4" /> Schedule</button></DialogTrigger>
          <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
            <DialogHeader><DialogTitle>Schedule Site Visit</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <select value={form.lead_id} onChange={(e) => setForm({ ...form, lead_id: e.target.value })} data-testid="visit-lead" className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm"><option value="">Select lead…</option>{leads.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm"><option value="">Select project…</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
              <select value={form.associate_id} onChange={(e) => setForm({ ...form, associate_id: e.target.value })} className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm"><option value="">Select associate…</option>{associates.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="visit-date" className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm" />
            </div>
            <DialogFooter><button onClick={create} className="btn-gold px-4 py-2 text-sm" data-testid="save-visit-btn">Schedule</button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {visits.length === 0 ? <Empty label="No site visits scheduled." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visits.map((v) => (
            <div key={v.id} className="mb-card p-4 animate-fade-up" data-testid={`visit-${v.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-heading font-semibold text-slate-100">{v.lead_name || "Lead"}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColor[v.status] || statusColor.Scheduled}`}>{v.status}</span>
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-1.5 mb-1"><MapPin className="h-3.5 w-3.5" /> {v.project_name || "—"}</div>
              <div className="text-xs text-slate-500">{(v.date || "").slice(0, 10)}</div>
              {v.feedback && <div className="text-xs text-slate-400 mt-2 bg-slate-900/50 rounded-lg p-2">{v.feedback}</div>}
              {v.status === "Scheduled" && <button onClick={() => complete(v)} className="mt-3 w-full text-xs py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 flex items-center justify-center gap-1.5" data-testid={`complete-visit-${v.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Mark Completed</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
